// src/pages/LandingPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Search, Shield, AlertTriangle, MessageSquare, Scale, CheckCircle, BookOpen, Loader2 } from 'lucide-react';
import { reportService } from '../services/reportService';
import ReportCard from '../components/lending/ReportCard';
import { Link } from 'react-router-dom';

const LandingPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const howItWorksRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    // Debounce search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchTerm.trim().length > 2) {
      setSearching(true);
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const data = await reportService.getPublicReports(searchTerm);
          setResults(Array.isArray(data) ? data : []);
          setHasSearched(true);
        } catch (error) {
          console.error('Search error:', error);
          setResults([]);
        } finally {
          setSearching(false);
        }
      }, 500);
    } else {
      setResults([]);
      setHasSearched(false);
      setSearching(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  const scrollToHowItWorks = () => {
    howItWorksRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex flex-col">
      {/* Hero Section */}
      <div className="flex-1 flex flex-col justify-center items-center px-4 sm:px-6 lg:px-8 py-20 bg-slate-950 relative overflow-hidden">
        {/* Background Decorative */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 opacity-20 pointer-events-none">
            <div className="absolute top-[10%] left-[10%] w-96 h-96 bg-emerald-900/40 rounded-full blur-3xl"></div>
            <div className="absolute bottom-[10%] right-[10%] w-96 h-96 bg-blue-900/40 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl w-full text-center space-y-8 z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-xs font-semibold text-emerald-400 uppercase tracking-wide mb-4">
            <Shield size={14} /> Community Trust Platform
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold tracking-tighter text-white">
            verify, then <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-500">trust.</span>
          </h1>
          
          <p className="text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
            The open community registry for credit history. Search reports, submit claims, and resolve disputes transparently. 
            <br/><span className="text-slate-500 text-base">Not a credit bureau. A community forum.</span>
          </p>

          {/* Search Box */}
          <div className="relative max-w-2xl mx-auto w-full group mt-8">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-blue-500/20 blur-xl rounded-full group-hover:bg-emerald-500/30 transition-all duration-500"></div>
            <div className="relative flex items-center bg-slate-900/90 backdrop-blur-xl border border-slate-600 rounded-2xl px-6 py-4 shadow-2xl">
              {searching ? (
                <Loader2 className="text-emerald-400 animate-spin" size={24} />
              ) : (
                <Search className="text-slate-400" size={24} />
              )}
              <input 
                type="text"
                placeholder="Search by Mobile Number (e.g., +27 82...)"
                className="w-full bg-transparent border-none focus:ring-0 text-slate-100 placeholder-slate-500 ml-4 text-lg outline-none"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <p className="text-xs text-slate-500 mt-3 text-left pl-6">
                * Searching public records only. Recent reports may be in the 7-day grace period.
            </p>
          </div>

          <div className="flex gap-4 justify-center pt-8">
            <Link to="/list" className="px-8 py-3 bg-white text-slate-900 rounded-lg font-bold hover:bg-slate-200 transition-all">
              Submit a Report
            </Link>
            <button 
              onClick={scrollToHowItWorks}
              className="px-8 py-3 bg-slate-800 text-slate-300 border border-slate-700 rounded-lg font-bold hover:bg-slate-700 transition-all"
            >
              How it Works
            </button>
          </div>
        </div>
      </div>

      {/* Results Section */}
      {(searchTerm.trim().length > 2 || hasSearched) && (
        <div className="bg-slate-900 py-12 border-t border-slate-800 min-h-[400px]">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl font-bold mb-8 text-slate-200 flex items-center gap-3">
                <BookOpen className="text-emerald-400" />
                Public Records
                {searching && <Loader2 className="animate-spin text-emerald-400" size={20} />}
            </h2>
            
            {searching ? (
              <div className="text-center py-10">
                <Loader2 className="animate-spin text-emerald-400 mx-auto mb-4" size={32} />
                <p className="text-slate-500">Searching records...</p>
              </div>
            ) : results.length === 0 ? (
               <div className="text-center py-10 bg-slate-950 rounded-xl border border-slate-800 border-dashed">
                 <Shield className="text-slate-700 mx-auto mb-4" size={48} />
                 <p className="text-slate-500 mb-2">No public reports found matching "{searchTerm}"</p>
                 <p className="text-xs text-slate-600">Note: Reports less than 7 days old are hidden to allow for private resolution.</p>
               </div>
            ) : (
              <div className="space-y-4 mb-4">
                <p className="text-sm text-slate-400">
                  Found <span className="text-white font-bold">{results.length}</span> result{results.length !== 1 ? 's' : ''}
                </p>
                <div className="grid grid-cols-1 gap-6">
                  {results.map(r => (
                    <ReportCard key={r.id} report={r} isPublic={true} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Philosophy / Features */}
      {!searchTerm && (
        <>
        <div ref={howItWorksRef} className="bg-slate-900 py-24 border-t border-slate-800">
            <div className="max-w-6xl mx-auto px-6 lg:px-16">
                <div className="grid md:grid-cols-2 gap-16 items-center">
                    <div>
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">A Platform for Truth, <br/>Not Just Collection.</h2>
                        <div className="space-y-8">
                            <div className="flex gap-4">
                                <div className="mt-1 bg-slate-800 p-2 rounded-lg h-fit">
                                    <MessageSquare className="text-emerald-400" size={24} />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white">Right of Reply</h3>
                                    <p className="text-slate-400 mt-2">We don't just list debts. We allow borrowers to post public responses, disputes, or proof of payment. It's a conversation, not a conviction.</p>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="mt-1 bg-slate-800 p-2 rounded-lg h-fit">
                                    <Scale className="text-blue-400" size={24} />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white">Neutral Ground</h3>
                                    <p className="text-slate-400 mt-2">PAYBACK does not act as a judge. We host user-generated reports. Lenders post claims; borrowers post defenses. The community decides who to trust.</p>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="mt-1 bg-slate-800 p-2 rounded-lg h-fit">
                                    <Shield className="text-orange-400" size={24} />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white">7-Day Grace Period</h3>
                                    <p className="text-slate-400 mt-2">New reports are hidden from public search for 7 days. Borrowers get a notification and a chance to settle privately before it goes permanent.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-slate-950 p-6 rounded-2xl border border-slate-800 relative shadow-2xl transform rotate-1 hover:rotate-0 transition-all duration-500">
                        {/* Mock Report Graphic */}
                        <div className="absolute -top-3 -right-3 bg-emerald-500 text-slate-900 font-bold px-3 py-1 rounded-full text-xs shadow-lg transform rotate-2 flex items-center gap-1 z-10">
                            <CheckCircle size={12} className="fill-slate-900 text-emerald-500" /> Verified Lender
                        </div>

                        {/* Card Header */}
                        <div className="flex justify-between items-start mb-4 border-b border-slate-800 pb-3">
                            <div>
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 font-bold text-xs border border-slate-700">SJ</div>
                                    <div>
                                        <p className="text-white text-sm font-bold">Sarah Jenkins</p>
                                        <p className="text-slate-500 text-[10px] flex items-center gap-1">
                                            <span>Reported 2 days ago</span> • <span className="text-slate-400">Public Record</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-red-400 font-mono font-bold text-lg">R3,200.00</p>
                                <span className="text-[10px] text-red-400/70 uppercase tracking-wide font-bold bg-red-900/10 px-1 rounded">Overdue</span>
                            </div>
                        </div>

                        {/* Body Content */}
                        <div className="space-y-4">
                            <div className="bg-slate-900/30 p-3 rounded border border-slate-800/50">
                                <p className="text-slate-400 text-xs leading-relaxed italic">
                                    "Tenant vacated unit 4B without paying final month rent. Security deposit was insufficient to cover damages to the walls..."
                                </p>
                            </div>

                            {/* Simulated Reply Thread */}
                            <div className="pl-3 border-l-2 border-orange-500/50 mt-3">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-[10px] bg-orange-900/30 text-orange-400 px-1.5 py-0.5 rounded border border-orange-500/20 font-medium flex items-center gap-1">
                                        <MessageSquare size={10} /> Dispute Filed
                                    </span>
                                    <span className="text-[10px] text-slate-500">Borrower Reply</span>
                                </div>
                                <p className="text-slate-300 text-xs leading-relaxed">
                                    "I have proof of Zelle transfer for the rent on the 1st. The painting charges are excessive for normal wear and tear."
                                </p>
                                <div className="flex gap-2 mt-2">
                                     <div className="h-8 w-8 bg-slate-800 rounded border border-slate-700 flex items-center justify-center">
                                         <div className="w-4 h-3 bg-slate-600 rounded-sm"></div>
                                     </div>
                                     <div className="h-8 w-8 bg-slate-800 rounded border border-slate-700 flex items-center justify-center">
                                         <div className="w-4 h-3 bg-slate-600 rounded-sm"></div>
                                     </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="mt-6 pt-3 border-t border-slate-800 text-center">
                            <p className="text-slate-500 text-[10px] uppercase tracking-widest">Transparent history for everyone</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        {/* Disclaimer Footer */}
        <div className="bg-slate-950 py-12 border-t border-slate-900">
            <div className="max-w-4xl mx-auto px-6 text-center">
                <AlertTriangle className="mx-auto text-slate-600 mb-4" size={32} />
                <h3 className="text-slate-400 font-bold uppercase tracking-widest text-sm mb-2">Legal Disclaimer</h3>
                <p className="text-slate-600 text-xs leading-relaxed max-w-2xl mx-auto">
                    PAYBACK is a user-generated content platform. We do not verify the accuracy of reports submitted by users. 
                    We are not a credit bureau or a debt collection agency. All reports are the sole responsibility of the poster. 
                    Borrowers have the right to dispute any claim. False reporting may lead to account termination and legal liability.
                </p>
            </div>
        </div>
        </>
      )}
    </div>
  );
};

export default LandingPage;